import { Avatar } from '@/components/ui/avatar';
import { Box, chakra, Flex, Text, VStack, type SystemStyleObject } from '@chakra-ui/react';
import { Check } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import type { DemoBookingFlow } from './use-demo-booking-flow';

/** The capsule sits on dark glass, the banner on lilac. */
export type DemoTone = 'dark' | 'light';

interface ToneStyles {
  slotIdle: SystemStyleObject;
  slotActive: SystemStyleObject;
  slotHover: SystemStyleObject;
  slotHeight: string;
  dayOpacity: number;
  muted: string;
  error: string;
  link: SystemStyleObject;
}

export const DEMO_TONES: Record<DemoTone, ToneStyles> = {
  dark: {
    slotIdle: { bg: 'rgba(255,255,255,.05)', borderColor: 'navDark.subBorder', color: 'white' },
    slotActive: { bg: 'navDark.aiBg', borderColor: 'navDark.aiText', color: 'white' },
    slotHover: { bg: 'navDark.surfaceHoverBg' },
    slotHeight: '50px',
    dayOpacity: 0.72,
    muted: 'navDark.subText',
    error: 'navDark.dangerText',
    link: { fontSize: '12px', color: 'rgba(255,255,255,.8)', _hover: { color: 'white' } },
  },
  light: {
    slotIdle: { bg: 'rgba(255,255,255,.45)', borderColor: 'transparent', color: 'gray.900' },
    slotActive: { bg: 'white', borderColor: 'gray.900', color: 'gray.900' },
    slotHover: { bg: 'white' },
    slotHeight: '54px',
    dayOpacity: 1,
    muted: 'navInactive',
    error: 'red.700',
    link: { fontSize: '12.5px', color: 'navInactive' },
  },
};

/** Vincent's portrait, with his initial should the picture fail to load. */
export const VincentAvatar = ({ size, ringColor }: { size: string; ringColor: string }) => (
  <Avatar
    name="Vincent"
    src="/images/demo/vincent.png"
    boxSize={size}
    flex="none"
    borderWidth="2px"
    borderColor={ringColor}
    css={{ '& img': { transform: 'scale(1.18)' } }}
  />
);

export const BookingError = ({ flow, tone }: { flow: DemoBookingFlow; tone: DemoTone }) =>
  flow.bookingError ? (
    <Text role="alert" fontSize="12.5px" color={DEMO_TONES[tone].error}>
      {flow.bookingError}
    </Text>
  ) : null;

/** "Other slots" when three are shown, "fewer slots" once every slot is. */
export const SlotsToggle = ({
  flow,
  tone,
  label,
}: {
  flow: DemoBookingFlow;
  tone: DemoTone;
  label: string;
}) => {
  const { t } = useTranslation();
  if (flow.featured.length === 0) return null;
  return (
    <chakra.button
      type="button"
      onClick={flow.toggleShowAll}
      textDecoration="underline"
      textUnderlineOffset="3px"
      cursor="pointer"
      css={DEMO_TONES[tone].link}
    >
      {flow.showAll ? t('demoInvitation.fewerSlots') : label}
    </chakra.button>
  );
};

/** The booking confirmed: on the dark capsule inline, on the banner in a white card. */
export const BookedNotice = ({ flow, tone }: { flow: DemoBookingFlow; tone: DemoTone }) => {
  const { t } = useTranslation();
  const isDark = tone === 'dark';
  return (
    <Flex
      role="status"
      gap={3}
      align="flex-start"
      {...(isDark ? {} : { bg: 'white', borderRadius: 'xl', p: 4 })}
    >
      <Flex
        boxSize={isDark ? '20px' : '32px'}
        flex="none"
        borderRadius="full"
        bg={isDark ? 'navDark.liveText' : 'green.50'}
        color={isDark ? 'gray.900' : 'green.700'}
        align="center"
        justify="center"
      >
        <Check width={isDark ? 13 : 17} height={isDark ? 13 : 17} />
      </Flex>
      <VStack align="stretch" gap="3px">
        <Text fontFamily="heading" fontSize="15px" fontWeight="semibold">
          {t('demoInvitation.booked.title')}
        </Text>
        <Text fontSize="13px" lineHeight="1.45" color={DEMO_TONES[tone].muted}>
          {t(isDark ? 'demoInvitation.booked.capsule' : 'demoInvitation.booked.banner', {
            slot: flow.bookingLabels?.full,
            email: flow.booking?.attendeeEmail,
          })}
        </Text>
      </VStack>
    </Flex>
  );
};

/** Sits beside the avatar, which clips its own content. */
export const OnlineDot = () => (
  <Box
    position="absolute"
    right="-3px"
    bottom="-3px"
    boxSize="12px"
    borderRadius="full"
    bg="navDark.liveText"
    borderWidth="2px"
    borderColor="#0F1012"
  />
);
